import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { paddleCheckout } from "../../utils/paddleCheckout";
import { specsheetCredits } from "./specsheet.redux";

/**
 * The paywall: what a shop has left, and how it buys more.
 *
 * The enforcement has existed since the ledger shipped -- the server refuses
 * a sheet when the balance is gone. What did not exist was any way for the
 * shop to see that coming or do anything about it: the whole paywall was a
 * red toast reading "no sheet credits left", with no price, no button and no
 * number anywhere before it. A locked door is not a shop.
 *
 * Two pieces. A counter that rides in the scan screens' headers, so the last
 * few sheets are visible before they run out, and a panel that sells more --
 * shown on demand from the counter, and put in front of the operator when a
 * scan is refused.
 *
 * The packs come from Paddle, per environment, so a sandbox account tests
 * against sandbox packs and a live account sees the live catalogue. Until the
 * packs exist there that list is empty, and an empty list is not an error: it
 * says "coming soon" and offers the one thing that does work, which is asking
 * us.
 */

const Bar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.02em;
  background: ${(p) => (p.$low ? "#fef5e1" : "#eef0f8")};
  color: ${(p) => (p.$low ? "#9a6100" : "#00187e")};
`;

const Link = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #00187e;
  text-decoration: underline;

  &:hover,
  &:focus {
    color: #000f4d;
  }
`;

const Panel = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 16px;
  margin-bottom: 14px;
`;

const Refused = styled(Panel)`
  border-color: #f5a524;
  background: #fffaf0;
`;

const PanelTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #202124;
  margin-bottom: 4px;
`;

const PanelNote = styled.div`
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 14px;
  line-height: 1.45;
`;

const Packs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
`;

const Pack = styled.div`
  flex: 1 1 170px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PackSheets = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #202124;
`;

const PackPrice = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #00187e;
  line-height: 1.2;
`;

const PackEach = styled.div`
  font-size: 11px;
  color: #9aa0ad;
  margin-bottom: 10px;
`;

const Buy = styled.button`
  margin-top: auto;
  /* Touch-first: this is tapped on a tablet at a shop counter. */
  min-height: 40px;
  border: none;
  border-radius: 8px;
  background: #00187e;
  color: #fff;
  font-size: 13px;
  font-weight: 600;

  &:disabled {
    background: #c7cad6;
  }

  &:not(:disabled):hover {
    background: #000f4d;
  }
`;

const Soon = styled.div`
  font-size: 13px;
  color: #6b7280;
  padding: 10px 0 4px;
`;

const Small = styled.div`
  font-size: 11.5px;
  color: #9aa0ad;
  line-height: 1.5;

  a {
    color: #00187e;
  }
`;

const Close = styled(Link)`
  margin-left: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const money = (amountMinor, currency, locale) => {
  const value = Number(amountMinor || 0) / 100;
  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch (e) {
    // An unknown currency code must not take the price off the card.
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
};

const perSheet = (amountMinor, credits, currency, locale) => {
  if (!credits) return "";
  const value = Number(amountMinor || 0) / 100 / credits;
  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    return value.toFixed(2);
  }
};

/**
 * The counter. Deliberately silent until the server has answered: showing
 * "0 left" while the request is still in flight would put a paywall in front
 * of a shop that has plenty.
 */
export const ScanCreditsCount = ({ credits, onBuy }) => {
  const { t } = useTranslation();

  if (!credits || !credits.loaded || credits.balance === null) return null;

  const balance = credits.balance;
  // "Low" is what makes the counter worth having: it is the warning, not the
  // number. Five is about a morning's work.
  const low = credits.enforcing && balance <= 5;

  return (
    <Bar>
      <Count $low={low}>
        {t("specsheetScan.credits.left", { count: balance })}
      </Count>
      {onBuy && (
        <Link type="button" onClick={onBuy}>
          {balance > 0
            ? t("specsheetScan.credits.buyMore")
            : t("specsheetScan.credits.buy")}
        </Link>
      )}
    </Bar>
  );
};

const ScanCreditsPanelBase = ({
  credits,
  refusedMessage,
  onClose,
  isSandbox,
  customerId,
  addressId,
  spectreUserId,
  locale,
  getCredits,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!credits || !credits.loaded) getCredits({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const packs = (credits && credits.packs) || [];
  const balance = credits && credits.balance !== null ? credits.balance : null;
  const Shell = refusedMessage ? Refused : Panel;

  const openCheckout = (pack) => {
    paddleCheckout({
      items: [{ priceId: pack.priceId, quantity: 1 }],
      // Sent when we have them so the overlay can skip re-asking for an
      // address. A shop buying a second pack should not be filling in a form
      // it already filled in for its subscription.
      customer: customerId
        ? { id: customerId, ...(addressId ? { address: { id: addressId } } : {}) }
        : undefined,
      // Read back by the purchase webhook. `kind` is what tells it this is a
      // consumable and not a module: a pack grants no entitlement, it only
      // adds to the ledger.
      customData: { kind: "credits", credits: String(pack.credits) },
      spectreUserId,
      settings: { locale },
      isSandbox,
      onComplete: () => {
        // The credits are written by the webhook, not by the checkout, so
        // this waits for the balance to actually move rather than assuming.
        getCredits({ awaitingAbove: balance === null ? 0 : balance });
      },
    });
  };

  return (
    <Shell>
      <Header>
        <PanelTitle>
          {refusedMessage
            ? t("specsheetScan.credits.outTitle")
            : t("specsheetScan.credits.title")}
        </PanelTitle>
        {onClose && (
          <Close type="button" onClick={onClose}>
            {t("specsheetScan.batch.close")}
          </Close>
        )}
      </Header>

      <PanelNote>
        {refusedMessage ||
          (balance !== null
            ? t("specsheetScan.credits.have", { count: balance })
            : "")}
      </PanelNote>

      {packs.length > 0 ? (
        <Packs>
          {packs.map((pack) => (
            <Pack key={pack.priceId}>
              <PackSheets>
                {t("specsheetScan.credits.sheets", { count: pack.credits })}
              </PackSheets>
              <PackPrice>
                {money(pack.amount, pack.currency, locale)}
              </PackPrice>
              <PackEach>
                {t("specsheetScan.credits.each", {
                  price: perSheet(
                    pack.amount,
                    pack.credits,
                    pack.currency,
                    locale,
                  ),
                })}
              </PackEach>
              <Buy type="button" onClick={() => openCheckout(pack)}>
                {t("specsheetScan.credits.buyPack")}
              </Buy>
            </Pack>
          ))}
        </Packs>
      ) : (
        <Soon>{t("specsheetScan.credits.soon")}</Soon>
      )}

      <Small>
        {/* Agreed with Mark: a sheet that the model reads is charged even
            when the result needs correcting, because most of that is the
            sheet. Saying so here is the other half of that decision -- it is
            not a fair charge if it is a surprise. */}
        {t("specsheetScan.credits.disclaimer")}
        <br />
        {t("specsheetScan.credits.more")}{" "}
        <a href="mailto:support@spectrebowling.com">
          support@spectrebowling.com
        </a>
      </Small>
    </Shell>
  );
};

export const ScanCreditsPanel = connect(
  (state) => ({
    credits: state.specsheet.credits,
    isSandbox: state.userSession.isSandbox,
    customerId:
      state.userSession.subscriptionInfo?.customer ||
      state.userSession.subscriptionInfo?.customer_id,
    addressId: state.userSession.subscriptionInfo?.address_id,
    spectreUserId: state.userSession.userData?.id,
    locale: state.settings.language,
  }),
  { getCredits: specsheetCredits },
)(ScanCreditsPanelBase);

export default ScanCreditsPanel;
